// Shared, dependency-free fee arithmetic used by the backend and browser.
const MAX_ATOMIC = (1n << 64n) - 1n;
const SERVICE_FEE_KINDS = Object.freeze(['studioMint', 'directPayment', 'donation']);
const DEFAULT_SERVICE_FEE = Object.freeze({ enabled: false, mode: 'fixed', fixedLamports: '0', basisPoints: 0 });
function unsignedAtomic(value) {
    if (typeof value !== 'string' || !/^\d{1,20}$/.test(value) || BigInt(value) > MAX_ATOMIC) throw new Error('Enter an exact, non-negative amount within the token limit.');
    return BigInt(value);
}
function decimalToAtomic(value, decimals = 9) {
    // Existing gate prices are stored as numbers, including very small SOL values.
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
        const match = String(value).match(/^(\d+)(?:\.(\d+))?e([+-]?\d+)$/i);
        if (match) {
            const digits = match[1] + (match[2] || '');
            const point = match[1].length + Number(match[3]);
            value = point <= 0 ? '0.' + '0'.repeat(-point) + digits : point >= digits.length ? digits.padEnd(point, '0') : digits.slice(0, point) + '.' + digits.slice(point);
        } else value = String(value);
    }
    if (!Number.isInteger(decimals) || decimals < 0 || decimals > 18 || typeof value !== 'string' || value.length > 80 || !/^\d+(?:\.\d*)?$/.test(value)) throw new Error('Enter a valid decimal amount.');
    const [whole, fraction = ''] = value.split('.');
    if (fraction.length > decimals) throw new Error('The amount has more decimal places than this currency supports.');
    return unsignedAtomic((BigInt(whole) * 10n ** BigInt(decimals) + BigInt(fraction.padEnd(decimals, '0') || '0')).toString()).toString();
}
function formatUnits(value, decimals = 9) {
    const digits = unsignedAtomic(value).toString().padStart(decimals + 1, '0');
    if (!decimals) return digits;
    const fraction = digits.slice(-decimals).replace(/0+$/, '');
    return digits.slice(0, -decimals) + (fraction ? '.' + fraction : '');
}
function percentageToBasisPoints(value) {
    const points = decimalToAtomic(value, 2);
    if (BigInt(points) > 10000n) throw new Error('Percentage must be between 0 and 100.');
    return Number(points);
}
function normalizeServiceFeeRule(input = DEFAULT_SERVICE_FEE) {
    if (!input || typeof input !== 'object' || !['fixed', 'percentage'].includes(input.mode)) throw new Error('Choose a fixed amount or percentage fee.');
    const fixedLamports = unsignedAtomic(input.fixedLamports).toString();
    const basisPoints = Number(input.basisPoints);
    if (!Number.isInteger(basisPoints) || basisPoints < 0 || basisPoints > 10000) throw new Error('Fee percentage must be between 0 and 100.');
    return { enabled: input.enabled === true, mode: input.mode, fixedLamports, basisPoints };
}
function calculateServiceFee(input, baseAmountAtomic, isNative = true) {
    const rule = normalizeServiceFeeRule(input);
    const base = unsignedAtomic(baseAmountAtomic);
    let paymentFee = 0n, solFee = 0n;
    if (rule.enabled) {
        const fee = rule.mode === 'percentage' ? base * BigInt(rule.basisPoints) / 10000n : BigInt(rule.fixedLamports);
        if (isNative || rule.mode === 'percentage') paymentFee = fee;
        if (isNative || rule.mode === 'fixed') solFee = fee;
    }
    if (base + paymentFee > MAX_ATOMIC) throw new Error('The total payment exceeds the token limit.');
    return { baseAmountAtomic: base.toString(), paymentFeeAtomic: paymentFee.toString(), solFeeLamports: solFee.toString(), totalPaymentAtomic: (base + paymentFee).toString() };
}
module.exports = { SERVICE_FEE_KINDS, DEFAULT_SERVICE_FEE, unsignedAtomic, decimalToAtomic, formatUnits, percentageToBasisPoints, normalizeServiceFeeRule, calculateServiceFee };
